/* eslint-disable @typescript-eslint/no-non-null-assertion */

/* eslint-disable react/no-array-index-key*/
import deburr from 'lodash/deburr';
import findIndex from 'lodash/findIndex';
import orderBy from 'lodash/orderBy';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Header, Input } from 'semantic-ui-react';

import { useTracking } from '../../../Context';
import { applySearchFilter } from '../../../core/filter/utils';
import { eventTags } from '../../../core/trackers/events';
import { ContainerTypes } from '../../../propTypes';
import BlockContainer from '../../BlockContainer';
import { isImage, isPDF } from '../utils';
import './FilesBlock.scss';
import FileCard from './components/FileCard';
import ImageLightbox from './components/ImageLightbox';
import PDFModal from './components/PDFModal';

export type File = {
  _id: string;
  title?: string;
  description?: string;
  originalFilename?: string;
  uri: string;
  value?: {
    originalFilename: string;
    uri: string;
  };
};

type FileWrapper = File & {
  value?: {
    originalFilename: string;
    uri: string;
  };
};

export function sanitizeFile(file: File | FileWrapper): File {
  return {
    ...file,
    title: file?.title || file?.originalFilename || file?.value?.originalFilename,
    originalFilename: file?.originalFilename || file.value?.originalFilename,
    uri: (file?.uri || file?.value?.uri)!,
  };
}

export type FileTracking = { onDownload: (file: File) => void; onView: (file: File) => void };

export function useFileTracking(block: unknown): FileTracking {
  const { trackEvent } = useTracking();
  return useMemo(() => {
    function onDownload(file: File) {
      trackEvent(eventTags.DOWNLOAD_FILE, {
        ...file,
        file: { _id: file._id },
        item: block,
      });
    }
    function onView(file: File) {
      trackEvent(eventTags.VIEW_FILE, {
        ...file,
        file: { _id: file._id },
        item: block, // Parent item => cms block
      });
    }
    return { onDownload, onView };
  }, [block, trackEvent]);
}

type FilesBlockProps = {
  id: string;
  containerType: ContainerTypes;
  downloadOnClick: boolean;
  files: File[];
  order?: 'filename' | '';
  search?: {
    enabled: boolean;
  };
  subtitle: string;
  title: string;
  centered?: boolean;
};

const FilesBlock: FC<FilesBlockProps> = (props) => {
  const {
    id,
    title,
    subtitle,
    files,
    containerType,
    downloadOnClick,
    order,
    search,
    centered,
  } = props;
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{ images: File[]; currentIndex: number } | null>(null);
  const [pdfToView, setPdfToView] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { onDownload, onView } = useFileTracking(props);

  const cleanFiles = useMemo(() => {
    if (!files) return [];
    let sortedFiles = files.map(sanitizeFile);
    console.log('search', search?.enabled, searchQuery);
    if (search?.enabled && searchQuery) {
      sortedFiles = applySearchFilter(sortedFiles, searchQuery, [
        'originalFilename',
        'title',
        'description',
      ]);
    }
    if (order) {
      if (order === 'filename')
        sortedFiles = orderBy(sortedFiles, (f) => deburr(f.originalFilename?.toLowerCase()));
    }
    return sortedFiles;
  }, [order, files, search?.enabled, searchQuery]);

  if (files.length === 0) return null;

  return (
    <BlockContainer id={id} type={containerType} className="block--cms-files">
      <Header as="h3" style={{ marginBottom: 0 }}>
        {title}
      </Header>
      <Header as="h4" style={{ marginTop: 0 }}>
        {subtitle}
      </Header>
      {search?.enabled && (
        <Input
          icon="search"
          iconPosition="left"
          placeholder={t('filters.search')}
          value={searchQuery}
          fluid
          onChange={(_e, { value }) => setSearchQuery(value)}
        />
      )}
      {cleanFiles.length === 0 && !!searchQuery && search?.enabled && (
        <p className="no-results">{t('blocks.faq.no-results')}</p>
      )}
      <Card.Group centered={centered}>
        {cleanFiles.map((file, index) => {
          const { uri } = file;

          function setCurrentFile() {
            onView(file);
            if (isImage(uri)) {
              // is image
              const images = cleanFiles.filter((f) => isImage(f.uri));
              setLightbox({ images, currentIndex: findIndex(images, (i) => i.uri === uri) });
            } else if (isPDF(uri)) {
              setPdfToView(file);
            }
          }

          return (
            <FileCard
              key={index}
              file={file}
              downloadOnClick={downloadOnClick}
              onDownload={() => onDownload(file)}
              onClickFile={setCurrentFile}
            />
          );
        })}
      </Card.Group>
      {lightbox && <ImageLightbox {...lightbox} onClose={() => setLightbox(null)} />}
      {pdfToView && <PDFModal file={pdfToView} onClose={() => setPdfToView(null)} />}
    </BlockContainer>
  );
};

FilesBlock.defaultProps = {
  containerType: 'segment',
  downloadOnClick: false,
  files: [],
  order: undefined,
  subtitle: undefined,
  title: undefined,
};

export default FilesBlock;
